/**
 * CeroHalo · carrito.
 * - Añadir ofertas (siempre cantidad 1) desde cualquier botón [data-mt-add].
 * - Límite de unidades reales por pedido (metacampo custom.unidades): si una oferta haría pasar
 *   del máximo, no se añade y se ofrece sustituir el carrito por esa oferta.
 * - Carrito lateral con <dialog>, quitar líneas y cambio de 1 unidad al Pack 2.
 * Todo lo que entra en el carrito entra por /cart/add.js, para que Shopify emita el evento estándar
 * product_added_to_cart (píxeles como el de Meta). En las sustituciones, si falla quitar las otras
 * líneas, se deshace el añadido y el carrito no cambia.
 * Es un límite del tema: el checkout de Shopify no lo conoce.
 */

const DRAWER_SECTION = 'mt-cart-drawer';

/** @typedef {{ variantId: string, units: number, button?: HTMLElement }} Offer */

const config = readConfig();
let pendingOffer = /** @type {Offer | null} */ (null);
let busy = false;
let closeTimer = 0;

function readConfig() {
  const el = document.querySelector('[data-mt-cart-config]');
  const defaults = { maxUnits: 3, addedLabel: 'Añadido', errorLabel: 'Error', rootUrl: '/', drawerEnabled: true };
  if (!el) return defaults;
  try {
    return { ...defaults, ...JSON.parse(el.textContent || '{}') };
  } catch {
    return defaults;
  }
}

function rootPath(path) {
  const root = config.rootUrl.endsWith('/') ? config.rootUrl : `${config.rootUrl}/`;
  return `${root}${path.replace(/^\//, '')}`;
}

/* ---------------- Estado del carrito (leído del contenido pintado por Liquid) ---------------- */

/** Contenedor actual del carrito: el lateral, o el de la página /cart si no hay lateral. */
function contents() {
  return document.querySelector('[data-mt-drawer-body] [data-mt-cart-contents]') || document.querySelector('[data-mt-cart-contents]');
}

/** Unidades reales en el carrito, calculadas en Liquid con custom.unidades. */
function currentUnits() {
  return Number(contents()?.getAttribute('data-units') || 0);
}

/** @returns {Array<{ key: string, variantId: string, units: number }>} */
function currentLines() {
  const box = contents();
  if (!box) return [];
  return [...box.querySelectorAll('[data-mt-line]')].map((line) => ({
    key: line.getAttribute('data-key') || '',
    variantId: line.getAttribute('data-variant-id') || '',
    units: Number(line.getAttribute('data-mt-units') || 1),
  }));
}

/* ---------------- Peticiones ---------------- */

function sectionIds() {
  const ids = [DRAWER_SECTION];
  const page = document.querySelector('[data-mt-cart-page]');
  const pageId = page?.getAttribute('data-section-id');
  if (pageId) ids.push(pageId);
  return ids.join(',');
}

/**
 * @param {string} path
 * @param {Record<string, unknown>} body
 */
async function postJSON(path, body) {
  const response = await fetch(rootPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sectionIds() }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.description || data.message || `HTTP ${response.status}`);
  return data;
}

/* ---------------- Pintar ---------------- */

/**
 * Sustituye el contenido del carrito y hace que la barra de envío avance desde donde estaba.
 * @param {Element} current
 * @param {Element} fresh
 */
function swapContents(current, fresh) {
  const oldFill = /** @type {HTMLElement | null} */ (current.querySelector('.mt-cart__fill'));
  const oldValue = oldFill ? oldFill.style.getPropertyValue('--mt-progress') : '';
  current.replaceWith(fresh);
  const newFill = /** @type {HTMLElement | null} */ (fresh.querySelector('.mt-cart__fill'));
  if (!newFill || !oldValue || !document.documentElement.classList.contains('mt-motion-on')) return;
  const target = newFill.style.getPropertyValue('--mt-progress');
  if (target === oldValue) return;
  newFill.style.transition = 'none';
  newFill.style.setProperty('--mt-progress', oldValue);
  void newFill.offsetWidth; // fija el punto de partida antes de animar
  newFill.style.transition = '';
  newFill.style.setProperty('--mt-progress', target);
}

/**
 * @param {string | undefined} html
 * @param {Element | null} container
 */
function renderInto(html, container) {
  if (!html || !container) return null;
  const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mt-cart-contents]');
  const current = container.querySelector('[data-mt-cart-contents]');
  if (fresh && current) swapContents(current, fresh);
  return fresh;
}

/** @param {Element | null} box */
function updateCount(box) {
  if (!box) return;
  const count = Number(box.getAttribute('data-item-count') || 0);
  for (const badge of document.querySelectorAll('[data-mt-cart-count]')) {
    badge.textContent = String(count);
    badge.toggleAttribute('hidden', count === 0);
  }
}

/** @param {Record<string, string> | undefined} sections */
async function render(sections) {
  let data = sections;
  if (!data) {
    const response = await fetch(rootPath(`?sections=${sectionIds()}`));
    data = response.ok ? await response.json() : {};
  }
  const drawerBox = renderInto(data?.[DRAWER_SECTION], document.querySelector('[data-mt-drawer-body]'));
  const page = document.querySelector('[data-mt-cart-page]');
  const pageBox = page ? renderInto(data?.[page.getAttribute('data-section-id') || ''], page) : null;
  updateCount(drawerBox || pageBox);
  document.dispatchEvent(new CustomEvent('mt:cart-updated'));
}

/** @param {string} message */
function announce(message) {
  const status = document.querySelector('[data-mt-status]');
  if (!status) return;
  status.textContent = '';
  window.requestAnimationFrame(() => {
    status.textContent = message;
  });
}

/* ---------------- Carrito lateral ---------------- */

function drawer() {
  return /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-mt-drawer]'));
}

export function openDrawer() {
  const dialog = drawer();
  if (!dialog || !config.drawerEnabled) return;
  window.clearTimeout(closeTimer);
  if (!dialog.open) dialog.showModal();
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => dialog.setAttribute('data-open', ''));
  });
}

export function closeDrawer() {
  const dialog = drawer();
  if (!dialog || !dialog.open) return;
  dialog.removeAttribute('data-open');
  const panel = dialog.querySelector('[data-mt-drawer-panel]');
  // mt-motion-on ya incluye "reducir movimiento" (ver layout/theme.liquid)
  if (!panel || !document.documentElement.classList.contains('mt-motion-on')) {
    dialog.close();
    return;
  }
  const finish = () => {
    window.clearTimeout(closeTimer);
    panel.removeEventListener('transitionend', onEnd);
    // Si durante la salida se ha vuelto a abrir, no se cierra.
    if (!dialog.hasAttribute('data-open')) dialog.close();
  };
  /** @param {TransitionEvent} event */
  const onEnd = (event) => {
    if (event.target === panel && event.propertyName === 'transform') finish();
  };
  panel.addEventListener('transitionend', onEnd);
  window.clearTimeout(closeTimer);
  closeTimer = window.setTimeout(finish, 450);
}

/* ---------------- Aviso de límite ---------------- */

function limitDialog() {
  return /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-mt-limit]'));
}

/** @param {Offer} offer */
function showLimit(offer) {
  pendingOffer = offer;
  const dialog = limitDialog();
  if (!dialog) return;
  if (drawer()?.open) closeDrawer();
  if (!dialog.open) dialog.showModal();
}

function hideLimit() {
  limitDialog()?.close(); // el evento close limpia pendingOffer
}

/* ---------------- Acciones ---------------- */

/**
 * @param {HTMLElement | undefined} button
 * @param {boolean} state
 */
function setBusy(button, state) {
  busy = state;
  if (!button) return;
  if (state) button.setAttribute('aria-busy', 'true');
  else button.removeAttribute('aria-busy');
}

/** @param {HTMLElement | undefined} button */
function flashAdded(button) {
  const label = button?.querySelector('[data-mt-add-label]');
  if (!button || !label) return;
  const original = label.textContent;
  label.textContent = config.addedLabel;
  button.setAttribute('data-added', '');
  window.setTimeout(() => {
    label.textContent = original;
    button.removeAttribute('data-added');
  }, 1600);
}

/** @param {Offer} offer */
async function addOffer(offer) {
  if (busy) return;
  if (currentUnits() + offer.units > config.maxUnits) {
    showLimit(offer);
    return;
  }
  setBusy(offer.button, true);
  try {
    const data = await postJSON('cart/add.js', { items: [{ id: Number(offer.variantId), quantity: 1 }] });
    await render(data.sections);
    flashAdded(offer.button);
    announce(config.addedLabel);
    openDrawer();
  } catch (error) {
    console.error('[CeroHalo] add', error);
    announce(config.errorLabel);
  } finally {
    setBusy(offer.button, false);
  }
}

/**
 * Cambia el carrito en una sola petición, con las cantidades por variante: si la oferta no se
 * puede añadir, el carrito queda como estaba.
 * @param {Record<string, number>} updates
 * @param {HTMLElement | undefined} button
 */
async function applyUpdates(updates, button) {
  setBusy(button, true);
  try {
    const data = await postJSON('cart/update.js', { updates });
    await render(data.sections);
    announce(config.addedLabel);
    return true;
  } catch (error) {
    console.error('[CeroHalo] update', error);
    announce(config.errorLabel);
    await render(undefined);
    return false;
  } finally {
    setBusy(button, false);
  }
}

/**
 * Pone una oferta en el carrito quitando otras líneas. Si la oferta no estaba, entra con
 * /cart/add.js, que es lo que hace que Shopify emita el evento estándar product_added_to_cart
 * (el que reciben los píxeles, como AddToCart de Meta); /cart/update.js no lo emite. Después se
 * quitan las demás líneas en una sola petición y, si eso falla, se deshace el añadido para que el
 * carrito quede como estaba. Si la oferta ya estaba, no se añade nada: basta con un update.
 * @param {string} variantId
 * @param {Record<string, number>} removals - variante → 0
 * @param {HTMLElement | undefined} button
 */
async function putOffer(variantId, removals, button) {
  if (currentLines().some((line) => line.variantId === variantId)) {
    return applyUpdates({ ...removals, [variantId]: 1 }, button);
  }
  setBusy(button, true);
  try {
    await postJSON('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
    try {
      const data = await postJSON('cart/update.js', { updates: removals });
      await render(data.sections);
    } catch (error) {
      await postJSON('cart/update.js', { updates: { [variantId]: 0 } }).catch(() => {});
      throw error;
    }
    announce(config.addedLabel);
    return true;
  } catch (error) {
    console.error('[CeroHalo] replace', error);
    announce(config.errorLabel);
    await render(undefined);
    return false;
  } finally {
    setBusy(button, false);
  }
}

async function replaceWithPending() {
  const offer = pendingOffer;
  if (!offer || busy) return;
  /** @type {Record<string, number>} */
  const removals = {};
  for (const line of currentLines()) {
    if (line.variantId !== offer.variantId) removals[line.variantId] = 0;
  }
  const button = /** @type {HTMLElement | null} */ (document.querySelector('[data-mt-limit-replace]')) || undefined;
  const ok = await putOffer(offer.variantId, removals, button);
  hideLimit();
  if (ok) openDrawer();
}

/** @param {HTMLElement} button */
async function swapLine(button) {
  if (busy) return;
  const fromKey = button.dataset.fromKey;
  const toVariant = button.dataset.toVariant;
  if (!fromKey || !toVariant) return;
  const line = currentLines().find((item) => item.key === fromKey);
  if (!line) return;
  const toUnits = Number(button.dataset.mtUnits || 1);
  if (currentUnits() - line.units + toUnits > config.maxUnits) {
    showLimit({ variantId: toVariant, units: toUnits });
    return;
  }
  await putOffer(toVariant, { [line.variantId]: 0 }, button);
}

/**
 * @param {string} key
 * @param {HTMLElement} button
 */
async function removeLine(key, button) {
  if (busy) return;
  const line = button.closest('[data-mt-line]');
  line?.setAttribute('data-removing', '');
  setBusy(button, true);
  try {
    const data = await postJSON('cart/change.js', { id: key, quantity: 0 });
    await render(data.sections);
  } catch (error) {
    console.error('[CeroHalo] remove', error);
    line?.removeAttribute('data-removing');
    announce(config.errorLabel);
  } finally {
    setBusy(button, false);
  }
}

/* ---------------- Eventos ---------------- */

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('[data-mt-cart-open]') && config.drawerEnabled && drawer()) {
    event.preventDefault();
    openDrawer();
    return;
  }

  if (target.closest('[data-mt-cart-close]')) {
    closeDrawer();
    return;
  }

  const addButton = /** @type {HTMLElement | null} */ (target.closest('[data-mt-add]'));
  if (addButton) {
    event.preventDefault();
    const { variantId, mtUnits } = addButton.dataset;
    if (!variantId || addButton.getAttribute('aria-disabled') === 'true') return;
    addOffer({ variantId, units: Number(mtUnits || 1), button: addButton });
    return;
  }

  const removeButton = /** @type {HTMLElement | null} */ (target.closest('[data-mt-remove]'));
  if (removeButton) {
    removeLine(removeButton.dataset.mtRemove || '', removeButton);
    return;
  }

  const swapButton = /** @type {HTMLElement | null} */ (target.closest('[data-mt-swap]'));
  if (swapButton) {
    swapLine(swapButton);
    return;
  }

  if (target.closest('[data-mt-limit-replace]')) {
    replaceWithPending();
    return;
  }

  if (target.closest('[data-mt-limit-keep]')) {
    hideLimit();
    return;
  }

  // Clic en el fondo oscuro de un diálogo: cerrar.
  const dialog = drawer();
  if (dialog && target === dialog) closeDrawer();
  const limit = limitDialog();
  if (limit && target === limit) hideLimit();

  if (target.closest('[data-mt-cart-close-link]') && dialog?.open) closeDrawer();
});

drawer()?.addEventListener('cancel', (event) => {
  event.preventDefault();
  closeDrawer();
});

limitDialog()?.addEventListener('close', () => {
  pendingOffer = null;
});

// Otras piezas del tema pueden pedir abrir el carrito.
document.addEventListener('mt:open-cart', () => openDrawer());
